import { ArrowRight, Check, Clock, PartyPopper } from "lucide-react";
import { useId, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router";
import type { CourseDetail } from "../../schema/index.ts";
import { pickText, type Locale } from "../../schema/core.ts";
import { useSession } from "../auth.ts";
import { ErrorState } from "../components/ErrorState.tsx";
import { ProgressBar } from "../components/ProgressBar.tsx";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { useCourseKit } from "../provider.tsx";
import { isNotFound, useCompletedLessons, useCourse } from "../queries.ts";
import { lessonPath, loginPath, useDocumentTitle } from "../routing.ts";
import { ButtonLink } from "../ui/Button.tsx";
import { Card } from "../ui/Card.tsx";
import { cn } from "../ui/cn.ts";
import { PageLoading } from "../ui/Spinner.tsx";
import { NotFoundPage } from "./NotFoundPage.tsx";

/** `/cursus/:courseSlug` */
export const CourseOverviewPage = (): ReactNode => {
  const { courseSlug = "" } = useParams();
  return <CourseOverview slug={courseSlug} />;
};

/** Course intro, progress and lesson list; also the home page when only one course is published. */
export const CourseOverview = ({ slug }: { slug: string }): ReactNode => {
  const { locale } = useCourseKit();
  const copy = useSiteCopy();
  const course = useCourse(slug);
  useDocumentTitle(course.data ? pickText(course.data.title, locale) : null);

  if (course.isPending) return <PageLoading label={copy.common.loading} />;
  if (course.isError) {
    return isNotFound(course.error) ? (
      <NotFoundPage />
    ) : (
      <ErrorState onRetry={() => void course.refetch()} />
    );
  }
  return <CourseOverviewBody course={course.data} locale={locale} />;
};

const CourseOverviewBody = ({
  course,
  locale,
}: {
  course: CourseDetail;
  locale: Locale;
}): ReactNode => {
  const copy = useSiteCopy();
  const location = useLocation();
  const { user } = useSession();
  const { completed, ready } = useCompletedLessons(course.slug);
  const progressId = useId();

  const lessons = course.lessons;
  const doneCount = lessons.filter((lesson) => completed.has(lesson.id)).length;
  const allDone = ready && lessons.length > 0 && doneCount === lessons.length;
  const nextLesson = lessons.find((lesson) => !completed.has(lesson.id)) ?? lessons[0];
  const ctaLabel = allDone
    ? copy.course.restart
    : doneCount > 0
      ? copy.course.continue
      : copy.course.start;
  const summary = pickText(course.summary, locale).trim();

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
      {course.coverImage && (
        <img
          src={course.coverImage}
          alt=""
          className="mb-8 aspect-[21/9] w-full rounded-3xl bg-muted object-cover shadow-sm"
        />
      )}
      <h1 className="text-4xl font-semibold sm:text-5xl">{pickText(course.title, locale)}</h1>
      {summary && (
        <p className="mt-4 max-w-[68ch] text-xl leading-relaxed text-muted-foreground">{summary}</p>
      )}

      {user && ready && lessons.length > 0 && (
        <Card className="mt-8">
          <h2 id={progressId} className="font-sans font-semibold">
            {copy.course.progressHeading}
          </h2>
          <div className="mt-3">
            <ProgressBar
              done={doneCount}
              total={lessons.length}
              labelledBy={progressId}
              valueText={copy.course.progress(doneCount, lessons.length)}
            />
          </div>
          <p className="mt-2 text-base text-muted-foreground">
            {copy.course.progress(doneCount, lessons.length)}
          </p>
        </Card>
      )}

      {allDone && (
        <div className="mt-6 flex gap-3 rounded-2xl border border-success/35 bg-success/8 p-5">
          <PartyPopper aria-hidden="true" className="mt-1 size-6 shrink-0 text-success" />
          <div>
            <p className="font-semibold text-success">{copy.course.completedTitle}</p>
            <p className="mt-1">{copy.course.completedBody}</p>
          </div>
        </div>
      )}

      {nextLesson ? (
        <ButtonLink
          to={lessonPath(course.slug, nextLesson.slug)}
          size="lg"
          className="mt-8 w-full sm:w-auto">
          {ctaLabel}
          <ArrowRight aria-hidden="true" className="size-5" />
        </ButtonLink>
      ) : (
        <p className="mt-8 text-muted-foreground">{copy.course.noLessons}</p>
      )}

      {!user && lessons.length > 0 && (
        <Card className="mt-8 bg-muted/60">
          <h2 className="font-sans text-lg font-semibold">{copy.course.saveProgressTitle}</h2>
          <p className="mt-1 text-muted-foreground">{copy.course.saveProgressBody}</p>
          <ButtonLink
            to={loginPath(`${location.pathname}${location.search}`)}
            variant="secondary"
            className="mt-4">
            {copy.course.saveProgressCta}
          </ButtonLink>
        </Card>
      )}

      {lessons.length > 0 && (
        <section className="mt-12" aria-labelledby={`${progressId}-lessons`}>
          <h2 id={`${progressId}-lessons`} className="text-3xl font-semibold">
            {copy.course.lessonsHeading}
          </h2>
          <ol className="mt-5 flex flex-col gap-3">
            {lessons.map((lesson) => {
              const done = completed.has(lesson.id);
              const lessonSummary = pickText(lesson.summary, locale).trim();
              return (
                <li key={lesson.id}>
                  <Link
                    to={lessonPath(course.slug, lesson.slug)}
                    className={cn(
                      "group flex items-start gap-4 rounded-2xl border border-border bg-card p-4",
                      "transition-colors hover:border-primary sm:p-5",
                    )}>
                    <span
                      aria-hidden="true"
                      className={cn(
                        "grid size-10 shrink-0 place-items-center rounded-full font-semibold",
                        done ? "bg-success text-primary-foreground" : "bg-muted text-foreground",
                      )}>
                      {done ? <Check className="size-5" /> : lesson.position}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="sr-only">{copy.course.lessonNumber(lesson.position)}: </span>
                      <span
                        className={cn(
                          "block text-lg font-semibold",
                          "group-hover:text-primary group-hover:underline",
                        )}>
                        {pickText(lesson.title, locale)}
                      </span>
                      {lessonSummary && (
                        <span className="mt-1 block text-muted-foreground">{lessonSummary}</span>
                      )}
                      <span
                        className={cn(
                          "mt-2 flex flex-wrap items-center gap-x-4 gap-y-1",
                          "text-base text-muted-foreground",
                        )}>
                        {lesson.durationMinutes !== null && (
                          <span className="inline-flex items-center gap-1">
                            <Clock aria-hidden="true" className="size-4" />
                            {copy.common.minutes(lesson.durationMinutes)}
                          </span>
                        )}
                        {done && (
                          <span className="inline-flex items-center gap-1 font-semibold text-success">
                            <Check aria-hidden="true" className="size-4" />
                            {copy.course.lessonDone}
                          </span>
                        )}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </div>
  );
};
